import React from 'react';
import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';
import { useTracking } from '@mygpt/app/utils';

interface CaseStudySectionProps {
  onOpenVideo?: (src: string) => void;
}

/**
 * 大型商業施設での導入成功事例 Section
 *
 * 左側: 管理画面+スマホ連携イメージ
 * 右側: 成果データ4つ + お客様の声 + CTAボタン
 */
export const CaseStudySection: React.FC<CaseStudySectionProps> = () => {
  const { trackSignupClick } = useTracking();

  return (
    <section id="case-study" className="w-full px-4 md:px-10 lg:px-40 py-20 bg-white border-y border-solution-border">
      <div className="max-w-[1200px] w-full mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-solution-orange font-bold tracking-wider uppercase text-sm mb-2 block">
            商業施設でのAIチャットボット導入成功事例
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-solution-text mb-4">
            NEWoMan高輪<span className="text-solution-orange"> 導入成功事例</span>
          </h2>
          <p className="text-solution-text-secondary text-lg">首都圏の商業施設で実証した対応削減の実績</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* 左側: 画像 - スタッフと顧客の連携イメージ */}
          <div className="relative">
            <div className="flex items-center justify-center gap-4 md:gap-6">
              {/* 管理画面 (スタッフ側) */}
              <div className="relative flex-1 max-w-[280px]">
                <div className="bg-gray-800 rounded-lg p-1 shadow-2xl">
                  <div className="bg-gray-700 rounded-t px-2 py-1 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-400"></span>
                    <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                    <span className="w-2 h-2 rounded-full bg-green-400"></span>
                  </div>
                  <img
                    src="/assets/管理画面.png"
                    alt="GBase SupportのAIチャットボット管理画面 - 会話履歴とフロアマップ連携状況を一元管理"
                    className="w-full h-auto rounded-b"
                  />
                </div>
                <div className="mt-3 text-center">
                  <p className="text-sm font-bold text-solution-text">スタッフ管理画面</p>
                  <p className="text-xs text-solution-text-secondary">会話履歴をリアルタイム確認</p>
                </div>
              </div>

              {/* 接続アイコン */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-solution-orange/10 flex items-center justify-center">
                  <MaterialIcon name="sync_alt" className="text-solution-orange text-xl" />
                </div>
                <p className="text-xs text-solution-orange font-medium">連携</p>
              </div>

              {/* スマホ (顧客側) */}
              <div className="relative w-[120px] md:w-[140px]">
                <div className="bg-gray-900 rounded-[1.5rem] p-1.5 shadow-2xl">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-4 bg-gray-900 rounded-b-lg z-10"></div>
                  <div className="rounded-[1.2rem] overflow-hidden bg-white aspect-[9/19]">
                    <img
                      src="/assets/スマホ.png"
                      alt="スマートフォンでAIコンシェルジュに質問 - 10言語対応のチャットボット"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="mt-3 text-center">
                  <p className="text-sm font-bold text-solution-text">お客様スマホ</p>
                  <p className="text-xs text-solution-text-secondary">どこでもAIに質問</p>
                </div>
              </div>
            </div>

            {/* キャプション */}
            <div className="mt-6 bg-solution-orange/5 rounded-xl p-4 border border-solution-orange/20 text-center">
              <p className="text-sm text-solution-text">
                <span className="text-solution-orange font-bold">案内所に行かなくてもOK</span> —
                お客様は自分のスマホで質問、スタッフは管理画面で対応状況を把握
              </p>
            </div>
          </div>

          {/* 右側: 成果 */}
          <div className="space-y-8">
            {/* 4つの成果データ */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border border-solution-border shadow-sm text-center">
                <p className="text-4xl font-black text-solution-orange mb-2">
                  50~70<span className="text-xl">%</span>
                </p>
                <p className="text-sm text-solution-text-secondary font-medium">案内所・電話対応削減</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-solution-border shadow-sm text-center">
                <p className="text-4xl font-black text-solution-orange mb-2">
                  10<span className="text-xl">+</span>
                </p>
                <p className="text-sm text-solution-text-secondary font-medium">多言語対応(Inbound)</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-solution-border shadow-sm text-center">
                <p className="text-4xl font-black text-solution-orange mb-2">
                  <MaterialIcon name="devices" className="text-solution-orange text-4xl" />
                </p>
                <p className="text-sm text-solution-text-secondary font-medium">
                  PC・スマホ・大型タッチ
                  <br />
                  パネル対応
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-solution-border shadow-sm text-center">
                <p className="text-4xl font-black text-solution-orange mb-2">
                  1<span className="text-xl">秒</span>
                </p>
                <p className="text-sm text-solution-text-secondary font-medium">以内に即時応答</p>
              </div>
            </div>

            {/* お客様の声 */}
            <div className="bg-solution-orange/5 rounded-xl p-6 border border-solution-orange/20">
              <p className="text-solution-text leading-relaxed">
                <span className="text-solution-orange font-bold">「導入初月から効果を実感」</span>
                <br />
                案内所への問い合わせが大幅に減少し、スタッフは接客に集中できるようになりました。特にインバウンド対応では、多言語AIが活躍しています。
              </p>
              <p className="text-sm text-solution-text-secondary mt-4">— 首都圏大型商業施設 運営担当者</p>
            </div>

            {/* CTAボタン */}
            <div className="flex flex-col sm:flex-row gap-4">
              <SmartLink
                to="/register"
                className="flex items-center justify-center rounded-lg h-12 px-8 bg-solution-orange hover:bg-solution-orange-dark transition-colors text-white text-base font-bold shadow-lg shadow-solution-orange/20"
                onClick={() => trackSignupClick('case_study')}
              >
                14日間無料トライアル開始
              </SmartLink>
              <SmartLink
                to="/showcase/newoman-takanawa"
                className="flex items-center justify-center gap-2 rounded-lg h-12 px-8 bg-white border border-solution-border hover:border-solution-orange transition-colors text-solution-text text-base font-bold"
              >
                <MaterialIcon name="open_in_new" className="text-lg" />
                導入事例を詳しく見る
              </SmartLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudySection;
